// client/src/components/media/ScheduleCalendar.jsx
//
// Multi-day "repeat post" picker used by SchedulePost. The user clicks the days
// they want to post on; each selected day gets exactly ONE time slot ("once per
// day"). The first day picked defaults to one hour past the current time; every
// day picked after that inherits the last time the user set, so stamping many
// days at the same time is one click each. "Select this month" / "Next 30 days"
// bulk-add one slot per future day at the current default time.
//
// State of truth (the selected slots) lives in the parent via value/onChange:
//   value = [{ date: 'YYYY-MM-DD', time: 'HH:MM' }, ...]

import React, { useMemo, useState, useEffect } from 'react';
import { FaTimes, FaTrash } from 'react-icons/fa';
import TimeSelect from '../TimeSelect';
import { getNextSlotInTenantTZ } from '../../utils/timezoneUtils';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// YYYY-MM-DD for a Date's calendar day in a given timezone.
function dateKeyInTZ(date, tz) {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

// Compare YYYY-MM-DD strings lexicographically (valid because zero-padded).
const keyLt = (a, b) => a < b;

// Add n days to a YYYY-MM-DD key without timezone drift (treat as UTC noon).
function addDaysKey(key, n) {
  const [y, m, d] = key.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}

function MAX_SLOTS() { return 31; }

export default function ScheduleCalendar({
  open,
  onClose,
  tenantTimezone = 'America/Chicago',
  value = [],
  onChange,
}) {
  const todayKey = useMemo(() => dateKeyInTZ(new Date(), tenantTimezone), [tenantTimezone]);

  // The time a freshly-picked day will receive. Seeded to now+1h on open.
  const [defaultTime, setDefaultTime] = useState('12:00');
  // The day whose time the TimeSelect at the top currently edits.
  const [activeDate, setActiveDate] = useState(null);
  // Visible month: { year, month } (month is 0-based).
  const [view, setView] = useState(() => {
    const [y, m] = todayKey.split('-').map(Number);
    return { year: y, month: m - 1 };
  });

  // (Re)seed defaults whenever the modal opens.
  useEffect(() => {
    if (!open) return;
    const next = getNextSlotInTenantTZ(tenantTimezone, 60);
    setDefaultTime(next.time);
    const [y, m] = todayKey.split('-').map(Number);
    setView({ year: y, month: m - 1 });
    setActiveDate(null);
  }, [open, tenantTimezone, todayKey]);

  // Selected slots keyed by date for O(1) lookups.
  const slotMap = useMemo(() => {
    const map = new Map();
    for (const s of value) map.set(s.date, s.time);
    return map;
  }, [value]);

  const sortedSlots = useMemo(
    () => [...value].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)),
    [value]
  );

  if (!open) return null;

  const commit = (map) => {
    const slots = Array.from(map.entries()).map(([date, time]) => ({ date, time }));
    onChange?.(slots);
  };

  const toggleDay = (key) => {
    if (keyLt(key, todayKey)) return; // past day
    const map = new Map(slotMap);
    if (map.has(key)) {
      map.delete(key);
      if (activeDate === key) setActiveDate(null);
    } else {
      if (map.size >= MAX_SLOTS()) return;
      map.set(key, defaultTime);
      setActiveDate(key);
    }
    commit(map);
  };

  // Bulk-add one slot per eligible day, capped at MAX_SLOTS. Already-selected
  // days keep their existing time.
  const addDays = (keys) => {
    const map = new Map(slotMap);
    for (const key of keys) {
      if (map.size >= MAX_SLOTS()) break;
      if (keyLt(key, todayKey)) continue;
      if (!map.has(key)) map.set(key, defaultTime);
    }
    commit(map);
  };

  const selectVisibleMonth = () => {
    const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
    const keys = [];
    for (let d = 1; d <= daysInMonth; d++) {
      keys.push(`${view.year}-${String(view.month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
    }
    addDays(keys);
  };

  const selectNext30 = () => {
    const keys = [];
    for (let i = 0; i < 30; i++) keys.push(addDaysKey(todayKey, i));
    addDays(keys);
  };

  const clearAll = () => { onChange?.([]); setActiveDate(null); };

  const removeSlot = (key) => {
    const map = new Map(slotMap);
    map.delete(key);
    if (activeDate === key) setActiveDate(null);
    commit(map);
  };

  // Editing the time updates the active day's slot AND becomes the new default
  // so the next day picked inherits it.
  const handleTimeChange = (time) => {
    setDefaultTime(time);
    if (activeDate && slotMap.has(activeDate)) {
      const map = new Map(slotMap);
      map.set(activeDate, time);
      commit(map);
    }
  };

  const gotoMonth = (delta) => {
    setView((v) => {
      const m = v.month + delta;
      const year = v.year + Math.floor(m / 12);
      const month = ((m % 12) + 12) % 12;
      return { year, month };
    });
  };

  // Don't allow paging before the current month.
  const atCurrentMonth = (() => {
    const [ty, tm] = todayKey.split('-').map(Number);
    return view.year === ty && view.month === tm - 1;
  })();

  // Build the month grid (leading blanks for the first weekday).
  const firstWeekday = new Date(view.year, view.month, 1).getDay();
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const monthLabel = new Date(view.year, view.month, 1).toLocaleString('en-US', {
    month: 'long', year: 'numeric',
  });

  const fmtSlotDate = (key) => {
    const [y, m, d] = key.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  };
  const fmtTime = (t) => {
    const [h, mi] = t.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${String(mi).padStart(2, '0')} ${period}`;
  };

  return (
    <div
      className="schedule-calendar-backdrop"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1080,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12,
      }}
      onClick={onClose}
    >
      <div
        className="schedule-calendar-modal"
        style={{
          background: '#fff', color: '#212529', borderRadius: 10, width: '100%',
          maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #e9ecef' }}>
          <strong>Pick the days to repeat this post</strong>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6c757d' }}>
            <FaTimes />
          </button>
        </div>

        <div style={{ padding: 16 }}>
          {/* Time for the next picked day */}
          <div className="mb-3">
            <label className="form-label small mb-1" style={{ color: '#6c757d' }}>
              {activeDate ? `Time for ${fmtSlotDate(activeDate)}` : 'Time for the next day you pick'}
            </label>
            <TimeSelect
              value={defaultTime}
              onChange={handleTimeChange}
              tenantTimezone={tenantTimezone}
              showTimezone={true}
              id="schedule-calendar-time"
            />
            <div className="form-text">Each day you pick uses this time; change it before picking the next day to vary it.</div>
          </div>

          {/* Month nav */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => gotoMonth(-1)} disabled={atCurrentMonth}>‹</button>
            <strong>{monthLabel}</strong>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => gotoMonth(1)}>›</button>
          </div>

          {/* Weekday header */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
            {WEEKDAYS.map((w) => (
              <div key={w} style={{ textAlign: 'center', fontSize: 11, color: '#6c757d', fontWeight: 600 }}>{w}</div>
            ))}
          </div>

          {/* Day grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
            {cells.map((d, i) => {
              if (d === null) return <div key={`b${i}`} />;
              const key = `${view.year}-${String(view.month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
              const isPast = keyLt(key, todayKey);
              const isSelected = slotMap.has(key);
              const isActive = key === activeDate;
              return (
                <button
                  key={key}
                  type="button"
                  disabled={isPast}
                  onClick={() => toggleDay(key)}
                  title={isSelected ? `Scheduled ${fmtTime(slotMap.get(key))}` : undefined}
                  style={{
                    aspectRatio: '1 / 1', borderRadius: 8, fontSize: 13, cursor: isPast ? 'not-allowed' : 'pointer',
                    border: isActive ? '2px solid #ff6800' : '1px solid #dee2e6',
                    background: isSelected ? '#ff6800' : isPast ? '#f1f3f5' : '#fff',
                    color: isSelected ? '#fff' : isPast ? '#adb5bd' : '#212529',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1.1,
                    fontWeight: isSelected ? 700 : 400,
                  }}
                >
                  <span>{d}</span>
                  {isSelected && <span style={{ fontSize: 9 }}>{fmtTime(slotMap.get(key))}</span>}
                </button>
              );
            })}
          </div>

          {/* Bulk actions */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={selectVisibleMonth}>Select this month</button>
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={selectNext30}>Next 30 days</button>
            {value.length > 0 && (
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={clearAll} style={{ marginLeft: 'auto' }}>Clear all</button>
            )}
          </div>

          {/* Selected list */}
          <div style={{ marginTop: 14 }}>
            <div className="form-label small mb-1" style={{ color: '#6c757d' }}>
              {value.length} day{value.length === 1 ? '' : 's'} selected (max {MAX_SLOTS()}, once per day)
            </div>
            {sortedSlots.length === 0 ? (
              <div className="text-gray-400" style={{ fontSize: 13 }}>No days selected yet. Click a day above.</div>
            ) : (
              <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #e9ecef', borderRadius: 8 }}>
                {sortedSlots.map((s) => (
                  <div
                    key={s.date}
                    onClick={() => setActiveDate(s.date)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', cursor: 'pointer',
                      borderBottom: '1px solid #f1f3f5',
                      background: s.date === activeDate ? '#fff5ee' : 'transparent',
                    }}
                  >
                    <span style={{ fontSize: 13 }}>{fmtSlotDate(s.date)}</span>
                    <span style={{ fontSize: 13, color: '#ff6800', fontWeight: 600 }}>{fmtTime(s.time)}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeSlot(s.date); }}
                      aria-label={`Remove ${s.date}`}
                      style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: '#dc3545', cursor: 'pointer' }}
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 16px', borderTop: '1px solid #e9ecef', position: 'sticky', bottom: 0, background: '#fff' }}>
          <button type="button" className="btn btn-primary" onClick={onClose} style={{ background: '#ff6800', borderColor: '#ff6800' }}>
            Done ({value.length})
          </button>
        </div>
      </div>
    </div>
  );
}
